import { useEffect, useMemo, useRef } from 'react';
import { GestureRecognizer } from '../core/gestures';
import { isPasteShortcut, keyMessage, localShortcut, mapPoint, MOD_CTRL, textDiff, wheelDelta, type LocalShortcut } from '../core/input';
import { platform } from '../core/platform';
import type { InputMsg } from '../core/protocol';
import type { RemoteSession, StreamMode } from '../core/session';

interface Props {
  session?: RemoteSession;
  remote: { width: number; height: number };
  cursor: string;
  mode: StreamMode;
  stream?: MediaStream;
  frameSink: React.MutableRefObject<((b64: string) => void) | null>;
  inputEnabled: boolean;
  muted: boolean;
  keyboardRequest: number; // increments when the user taps the keyboard button
  onResize: (w: number, h: number) => void;
  onShortcut: (s: Exclude<LocalShortcut, null>) => void;
  onGesture: () => void; // any user gesture (used to flush pending clipboard writes, unmute)
}

const MIRROR_MAX = 64;

export function Viewport(p: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const kbRef = useRef<HTMLTextAreaElement>(null);
  const mirror = useRef('');
  const pinchRef = useRef<HTMLDivElement>(null);
  const props = useRef(p);
  props.current = p;
  const coarse = useMemo(() => typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches, []);

  const send = (m: InputMsg) => {
    if (props.current.inputEnabled) props.current.session?.input(m);
  };
  const surface = () => (props.current.mode === 'screencast' ? canvasRef.current : videoRef.current) ?? wrapRef.current!;
  const toRemote = (cx: number, cy: number) => mapPoint(cx, cy, surface().getBoundingClientRect(), props.current.remote);

  // --- size → remote viewport
  useEffect(() => {
    const el = wrapRef.current!;
    let timer: ReturnType<typeof setTimeout>;
    const ro = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) props.current.onResize(Math.floor(r.width), Math.floor(r.height));
      }, 100);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      clearTimeout(timer);
    };
  }, []);

  // --- media
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !p.stream) return;
    v.srcObject = p.stream;
    v.play().catch(() => {
      // autoplay with sound blocked: play muted until the first gesture
      v.muted = true;
      v.play().catch(() => {});
    });
  }, [p.stream]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = p.muted;
  }, [p.muted]);

  // --- screencast fallback frames
  useEffect(() => {
    let busy = false;
    let next: string | null = null;
    const draw = (b64: string) => {
      if (busy) {
        next = b64;
        return;
      }
      busy = true;
      const img = new Image();
      img.onload = () => {
        const c = canvasRef.current;
        if (c) {
          if (c.width !== img.width || c.height !== img.height) {
            c.width = img.width;
            c.height = img.height;
          }
          c.getContext('2d')!.drawImage(img, 0, 0);
        }
        busy = false;
        if (next) {
          const n = next;
          next = null;
          draw(n);
        }
      };
      img.onerror = () => (busy = false);
      img.src = 'data:image/jpeg;base64,' + b64;
    };
    p.frameSink.current = draw;
    return () => {
      p.frameSink.current = null;
    };
  }, [p.frameSink]);

  // --- keyboard button (mobile)
  useEffect(() => {
    if (p.keyboardRequest > 0) kbRef.current?.focus({ preventScroll: true });
  }, [p.keyboardRequest]);

  // --- pointer, wheel, touch
  useEffect(() => {
    const el = wrapRef.current!;
    let pendingMove: { x: number; y: number } | null = null;
    let raf = 0;
    let wheelAcc: { x: number; y: number; dx: number; dy: number; m: number } | null = null;

    const flush = () => {
      raf = 0;
      if (pendingMove) send({ t: 'mm', ...pendingMove });
      pendingMove = null;
      if (wheelAcc) send({ t: 'wh', ...wheelAcc });
      wheelAcc = null;
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(flush);
    };

    const gestures = new GestureRecognizer((g) => {
      if (g.kind === 'input') send(g.msg);
      else if (g.kind === 'longPress') platform().haptic();
      else if (g.kind === 'pinchPreview' && pinchRef.current) {
        const r = surface().getBoundingClientRect();
        const ox = (g.cx / props.current.remote.width) * r.width;
        const oy = (g.cy / props.current.remote.height) * r.height;
        pinchRef.current.style.transformOrigin = `${ox}px ${oy}px`;
        pinchRef.current.style.transform = `scale(${g.scale})`;
      } else if (g.kind === 'pinchEnd' && pinchRef.current) {
        pinchRef.current.style.transform = '';
      }
    });

    const onDown = (e: PointerEvent) => {
      props.current.onGesture();
      if (!props.current.inputEnabled) return;
      e.preventDefault();
      el.setPointerCapture(e.pointerId);
      const pt = toRemote(e.clientX, e.clientY);
      if (e.pointerType === 'touch') return gestures.down({ id: e.pointerId, ...pt });
      if (!coarse) kbRef.current?.focus({ preventScroll: true });
      flush();
      send({ t: 'md', ...pt, b: e.button });
    };
    const onMove = (e: PointerEvent) => {
      if (!props.current.inputEnabled) return;
      const pt = toRemote(e.clientX, e.clientY);
      if (e.pointerType === 'touch') return gestures.move({ id: e.pointerId, ...pt });
      pendingMove = pt;
      schedule();
    };
    const onUp = (e: PointerEvent) => {
      if (!props.current.inputEnabled) return;
      const pt = toRemote(e.clientX, e.clientY);
      if (e.pointerType === 'touch') return gestures.up({ id: e.pointerId, ...pt });
      flush();
      send({ t: 'mu', ...pt, b: e.button });
    };
    const onCancel = (e: PointerEvent) => {
      if (e.pointerType === 'touch') gestures.cancel({ id: e.pointerId, x: 0, y: 0 });
    };
    const onWheel = (e: WheelEvent) => {
      if (!props.current.inputEnabled) return;
      e.preventDefault();
      const pt = toRemote(e.clientX, e.clientY);
      const d = wheelDelta(e, props.current.remote.height);
      const m = (e.ctrlKey ? MOD_CTRL : 0) | (e.shiftKey ? 8 : 0);
      if (wheelAcc && wheelAcc.m === m) {
        wheelAcc.dx += d.dx;
        wheelAcc.dy += d.dy;
        wheelAcc.x = pt.x;
        wheelAcc.y = pt.y;
      } else {
        if (wheelAcc) flush();
        wheelAcc = { ...pt, dx: d.dx, dy: d.dy, m };
      }
      schedule();
    };
    const onContext = (e: Event) => e.preventDefault();
    // Safari pinch gestures on trackpads
    const onGestureEvt = (e: Event) => e.preventDefault();

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onCancel);
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('contextmenu', onContext);
    el.addEventListener('gesturestart', onGestureEvt);
    return () => {
      gestures.dispose();
      cancelAnimationFrame(raf);
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onCancel);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('contextmenu', onContext);
      el.removeEventListener('gesturestart', onGestureEvt);
    };
  }, [coarse]);

  // --- keyboard
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const ev = e.nativeEvent;
    props.current.onGesture();
    const sc = localShortcut(ev);
    if (sc) {
      e.preventDefault();
      p.onShortcut(sc);
      return;
    }
    if (isPasteShortcut(ev)) return; // handled by onPaste (syncs clipboard first)
    const modified = ev.ctrlKey || ev.metaKey || ev.altKey;
    if (coarse && !modified) {
      // Soft keyboards: printable characters arrive through `input` (autocorrect-safe).
      const named = ev.key.length > 1 && ev.key !== 'Unidentified' && ev.key !== 'Process';
      if (!named) return;
      if (ev.key === 'Backspace' && mirror.current) {
        e.preventDefault();
        mirror.current = Array.from(mirror.current).slice(0, -1).join('');
        kbRef.current!.value = mirror.current;
      } else {
        e.preventDefault();
        if (ev.key === 'Enter') resetMirror();
      }
    }
    const msg = keyMessage(ev, true);
    if (!msg) return;
    e.preventDefault();
    send(msg);
  };

  const onKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const ev = e.nativeEvent;
    if (isPasteShortcut(ev) || localShortcut(ev)) return;
    if (coarse && !(ev.ctrlKey || ev.metaKey || ev.altKey) && !(ev.key.length > 1 && ev.key !== 'Unidentified' && ev.key !== 'Process')) return;
    const msg = keyMessage(ev, false);
    if (msg) send(msg);
  };

  const resetMirror = () => {
    mirror.current = '';
    if (kbRef.current) kbRef.current.value = '';
  };

  const onInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const ta = e.currentTarget;
    if ((e.nativeEvent as InputEvent).isComposing && !coarse) return; // desktop IME: wait for compositionend
    const next = ta.value;
    if (!coarse) {
      // Desktop: regular keys were sent (and suppressed) on keydown, so anything that
      // still lands here came without key events – emoji picker, dictation, autofill.
      if (next) send({ t: 'text', text: next });
      ta.value = '';
      return;
    }
    const { backspaces, insert } = textDiff(mirror.current, next);
    for (let i = 0; i < backspaces; i++) {
      send({ t: 'kd', key: 'Backspace', code: 'Backspace', kc: 8, m: 0 });
      send({ t: 'ku', key: 'Backspace', code: 'Backspace', kc: 8, m: 0 });
    }
    if (insert) {
      const lines = insert.split('\n');
      lines.forEach((line, i) => {
        if (line) send({ t: 'text', text: line });
        if (i < lines.length - 1) {
          send({ t: 'kd', key: 'Enter', code: 'Enter', kc: 13, m: 0 });
          send({ t: 'ku', key: 'Enter', code: 'Enter', kc: 13, m: 0 });
        }
      });
    }
    mirror.current = next.length > MIRROR_MAX ? next.slice(-MIRROR_MAX) : next;
    if (next.length > MIRROR_MAX) ta.value = mirror.current;
  };

  const onCompositionEnd = (e: React.CompositionEvent<HTMLTextAreaElement>) => {
    if (coarse) return; // handled by input diffing
    if (e.data) send({ t: 'text', text: e.data });
    e.currentTarget.value = '';
  };

  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    const s = props.current.session;
    if (!s || !props.current.inputEnabled) return;
    // Same channel (WebSocket) for both so the remote clipboard is set before Ctrl+V.
    if (text) s.send({ t: 'clipboard', text });
    s.send({ t: 'kd', key: 'v', code: 'KeyV', kc: 86, m: MOD_CTRL });
    s.send({ t: 'ku', key: 'v', code: 'KeyV', kc: 86, m: MOD_CTRL });
  };

  return (
    <div ref={wrapRef} className="viewport" style={{ cursor: p.inputEnabled ? p.cursor : 'default' }}>
      <div ref={pinchRef} className="viewport-surface">
        <video ref={videoRef} className="remote-video" playsInline autoPlay muted={p.muted} hidden={p.mode === 'screencast'} />
        <canvas ref={canvasRef} className="remote-video" hidden={p.mode !== 'screencast'} />
      </div>
      <textarea
        ref={kbRef}
        className="kb-capture"
        aria-label="keyboard input"
        autoCapitalize={coarse ? 'sentences' : 'off'}
        autoCorrect={coarse ? 'on' : 'off'}
        autoComplete="off"
        spellCheck={coarse}
        enterKeyHint="enter"
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onInput={onInput}
        onCompositionEnd={onCompositionEnd}
        onPaste={onPaste}
        onBlur={resetMirror}
      />
    </div>
  );
}
